import { formatDistanceToNow, isToday, isYesterday, isThisWeek, isThisMonth, eachDayOfInterval, startOfMonth, endOfMonth, differenceInYears, addDays, addMonths, addYears, differenceInDays, differenceInHours, differenceInMinutes, differenceInSeconds, parseISO, isAfter, isBefore, subYears, } from 'date-fns';
import { formatInTimeZone, toZonedTime, getTimezoneOffset } from 'date-fns-tz';
import { ko, enUS, ja, zhCN } from 'date-fns/locale';
/**
 * 날짜 유틸리티 함수들
 * date-fns와 date-fns-tz를 기반으로 한 날짜 처리 유틸리티입니다.
 */
/** 로케일 매핑 */
const localeMap = {
    ko,
    en: enUS,
    ja,
    zh: zhCN,
};
/**
 * 날짜를 지정된 형식으로 포맷팅합니다.
 * @param date - 포맷팅할 날짜
 * @param formatStr - 포맷 문자열 (기본값: 'yyyy-MM-dd')
 * @param locale - 로케일 (기본값: 'ko')
 * @param timeZone - 시간대 (기본값: 'Asia/Seoul')
 * @returns 포맷팅된 날짜 문자열
 * @throws {Error} 유효하지 않은 날짜인 경우
 *
 * @example
 * ```typescript
 * formatDate(new Date(), 'yyyy년 MM월 dd일'); // "2024년 01월 15일"
 * formatDate('2024-01-15', 'yyyy/MM/dd', 'en'); // "2024/01/15"
 * ```
 */
export function formatDate(date, formatStr = 'yyyy-MM-dd', locale = 'ko', timeZone = 'Asia/Seoul') {
    let parsedDate;
    if (!date) {
        return '-';
    }
    // 문자열인 경우 YYYYMMDD 형식 체크
    if (typeof date === 'string') {
        // YYYYMMDD 형식 (8자리 숫자) 체크
        if (/^\d{8}$/.test(date)) {
            const year = date.substring(0, 4);
            const month = date.substring(4, 6);
            const day = date.substring(6, 8);
            parsedDate = new Date(`${year}-${month}-${day}`);
        }
        else {
            parsedDate = new Date(date);
        }
    }
    else {
        parsedDate = new Date(date);
    }
    if (isNaN(parsedDate.getTime())) {
        throw new Error('Invalid date');
    }
    return formatInTimeZone(parsedDate, timeZone, formatStr, {
        locale: localeMap[locale],
    });
}
/**
 * 상대적 시간을 표시합니다 (예: "3분 전", "1시간 전").
 * @param date - 기준 날짜
 * @param locale - 로케일 (기본값: 'ko')
 * @returns 상대적 시간 문자열
 * @throws {Error} 유효하지 않은 날짜인 경우
 *
 * @example
 * ```typescript
 * formatRelativeTime(new Date(Date.now() - 60000)); // "1분 전"
 * formatRelativeTime('2024-01-01'); // "15일 전"
 * ```
 */
export function formatRelativeTime(date, locale = 'ko') {
    const d = new Date(date);
    if (isNaN(d.getTime())) {
        throw new Error('Invalid date');
    }
    return formatDistanceToNow(d, {
        addSuffix: true,
        locale: localeMap[locale],
    });
}
/**
 * 경과 시간(초)을 "X시간 Y분 Z초" 형식의 문자열로 변환합니다.
 * 예: 3661 -> "1시간 1분 1초"
 *     125 -> "2분 5초"
 *     45 -> "45초"
 *
 * @param seconds - 경과 시간(초)
 * @param hideTrailingZeros - 맨 뒤 0 단위 생략 여부 (기본값: true)
 *   - 맨 앞 0은 항상 생략: 0시간 9분 → "9분", 0시간 0분 18초 → "18초"
 *   - true: 10시간 23분 0초 → "10시간 23분"
 *   - false: 10시간 23분 0초 → "10시간 23분 0초"
 * @returns 포맷팅된 시간 문자열
 */
export function formatDuration(seconds, hideTrailingZeros = true) {
    if (!seconds || seconds === 0)
        return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    // 맨 앞 0은 항상 생략 (0시간 9분 → 9분)
    const firstNonZero = hours > 0 ? 0 : minutes > 0 ? 1 : 2;
    const lastNonZero = hideTrailingZeros
        ? (secs > 0 ? 2 : minutes > 0 ? 1 : 0)
        : 2; // hideTrailingZeros=false면 항상 초까지 표시
    const parts = [];
    if (firstNonZero <= 0 && lastNonZero >= 0)
        parts.push(`${hours}시간`);
    if (firstNonZero <= 1 && lastNonZero >= 1)
        parts.push(`${minutes}분`);
    if (firstNonZero <= 2 && lastNonZero >= 2)
        parts.push(`${secs}초`);
    return parts.join(' ');
}
/**
 * 날짜가 오늘인지 확인합니다.
 * @param date - 확인할 날짜
 * @returns 오늘인지 여부
 */
export function isTodayDate(date) {
    const d = new Date(date);
    return isToday(d);
}
/**
 * 날짜가 어제인지 확인합니다.
 * @param date - 확인할 날짜
 * @returns 어제인지 여부
 */
export function isYesterdayDate(date) {
    const d = new Date(date);
    return isYesterday(d);
}
/**
 * 날짜가 이번 주인지 확인합니다.
 * @param date - 확인할 날짜
 * @returns 이번 주인지 여부
 */
export function isThisWeekDate(date) {
    const d = new Date(date);
    return isThisWeek(d);
}
/**
 * 날짜가 이번 달인지 확인합니다.
 * @param date - 확인할 날짜
 * @returns 이번 달인지 여부
 */
export function isThisMonthDate(date) {
    const d = new Date(date);
    return isThisMonth(d);
}
export function createRecentRangeDisabledDates(options = {}) {
    const { getStartDate, getEndDate, rangeYears = 1, getMaxDate } = options;
    const getMaxDay = () => {
        const baseDate = getMaxDate ? getMaxDate() : new Date();
        const maxDate = new Date(baseDate);
        maxDate.setHours(23, 59, 59, 999);
        return maxDate;
    };
    const getMinDay = () => {
        const baseDate = getMaxDate ? getMaxDate() : new Date();
        const minDate = subYears(new Date(baseDate), rangeYears);
        minDate.setHours(0, 0, 0, 0);
        return minDate;
    };
    const disabledStartDate = (date) => {
        const minDate = getMinDay();
        const maxDate = getMaxDay();
        if (isBefore(date, minDate)) {
            return true;
        }
        if (isAfter(date, maxDate)) {
            return true;
        }
        const endDateValue = getEndDate?.();
        if (endDateValue) {
            const endDate = parseISO(endDateValue);
            endDate.setHours(23, 59, 59, 999);
            if (isAfter(date, endDate)) {
                return true;
            }
        }
        return false;
    };
    const disabledEndDate = (date) => {
        const minDate = getMinDay();
        const maxDate = getMaxDay();
        if (isAfter(date, maxDate)) {
            return true;
        }
        const startDateValue = getStartDate?.();
        if (startDateValue) {
            const startDate = parseISO(startDateValue);
            startDate.setHours(0, 0, 0, 0);
            if (isBefore(date, startDate)) {
                return true;
            }
        }
        if (isBefore(date, minDate)) {
            return true;
        }
        return false;
    };
    return { disabledStartDate, disabledEndDate };
}
/**
 * 두 날짜 사이의 모든 날짜를 배열로 반환합니다.
 * @param startDate - 시작 날짜
 * @param endDate - 종료 날짜
 * @returns 날짜 배열
 *
 * @example
 * ```typescript
 * const dates = getDateRange('2024-01-01', '2024-01-05');
 * // [2024-01-01, 2024-01-02, 2024-01-03, 2024-01-04, 2024-01-05]
 * ```
 */
export function getDateRange(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return eachDayOfInterval({ start, end });
}
/**
 * 지정된 날짜가 속한 월의 첫 번째 날과 마지막 날을 반환합니다.
 * @param date - 기준 날짜
 * @returns 월의 시작일과 종료일
 *
 * @example
 * ```typescript
 * const { start, end } = getMonthBoundaries('2024-01-15');
 * // start: 2024-01-01, end: 2024-01-31
 * ```
 */
export function getMonthBoundaries(date) {
    const d = new Date(date);
    return {
        start: startOfMonth(d),
        end: endOfMonth(d),
    };
}
/**
 * 생년월일로부터 현재 나이를 계산합니다.
 * @param birthDate - 생년월일
 * @returns 나이
 *
 * @example
 * ```typescript
 * const age = calculateAge('1990-05-15'); // 33
 * ```
 */
export function calculateAge(birthDate) {
    const birth = new Date(birthDate);
    const today = new Date();
    return differenceInYears(today, birth);
}
/**
 * 날짜를 한 시간대에서 다른 시간대로 변환합니다.
 * @param date - 변환할 날짜
 * @param fromTimezone - 원본 시간대
 * @param toTimezone - 대상 시간대
 * @returns 변환된 날짜
 *
 * @example
 * ```typescript
 * const seoulTime = convertTimezone('2024-01-15T10:00:00Z', 'UTC', 'Asia/Seoul');
 * ```
 */
export function convertTimezone(date, fromTimezone, toTimezone) {
    const d = new Date(date);
    // fromTimezone에서 toTimezone으로 직접 변환
    return toZonedTime(d, toTimezone);
}
/**
 * 두 날짜 간의 차이를 지정된 단위로 계산합니다.
 * @param date1 - 첫 번째 날짜
 * @param date2 - 두 번째 날짜
 * @param unit - 차이 단위 (기본값: 'days')
 * @returns 차이 값
 *
 * @example
 * ```typescript
 * const daysDiff = getDateDiff('2024-01-01', '2024-01-15'); // 14
 * const hoursDiff = getDateDiff('2024-01-01T10:00:00', '2024-01-01T15:00:00', 'hours'); // 5
 * ```
 */
export function getDateDiff(date1, date2, unit = 'days') {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    switch (unit) {
        case 'days':
            return differenceInDays(d2, d1);
        case 'hours':
            return differenceInHours(d2, d1);
        case 'minutes':
            return differenceInMinutes(d2, d1);
        case 'seconds':
            return differenceInSeconds(d2, d1);
        default:
            return differenceInDays(d2, d1);
    }
}
/**
 * 날짜에 지정된 일수를 추가합니다.
 * @param date - 기준 날짜
 * @param days - 추가할 일수
 * @returns 새로운 날짜
 */
export function addDaysToDate(date, days) {
    const d = new Date(date);
    return addDays(d, days);
}
/**
 * 날짜에 지정된 개월수를 추가합니다.
 * @param date - 기준 날짜
 * @param months - 추가할 개월수
 * @returns 새로운 날짜
 */
export function addMonthsToDate(date, months) {
    const d = new Date(date);
    return addMonths(d, months);
}
/**
 * 날짜에 지정된 년수를 추가합니다.
 * @param date - 기준 날짜
 * @param years - 추가할 년수
 * @returns 새로운 날짜
 */
export function addYearsToDate(date, years) {
    const d = new Date(date);
    return addYears(d, years);
}
/**
 * 현재 시간을 지정된 시간대에서 가져옵니다.
 * @param timeZone - 시간대 (기본값: 'Asia/Seoul')
 * @returns 현재 시간
 */
export function nowInTimezone(timeZone = 'Asia/Seoul') {
    return toZonedTime(new Date(), timeZone);
}
/**
 * 지정된 시간대의 오프셋을 분 단위로 가져옵니다.
 * @param timezone - 시간대
 * @returns 오프셋 (분)
 *
 * @example
 * ```typescript
 * const offset = getTimezoneOffsetInMinutes('Asia/Seoul'); // 540 (UTC+9)
 * ```
 */
export function getTimezoneOffsetInMinutes(timezone) {
    return getTimezoneOffset(timezone);
}
/**
 * 날짜를 다른 시간대로 변환하여 RFC 3339 형식으로 반환합니다.
 * 입력 문자열의 타임존 정보를 자동으로 인식하여 변환합니다.
 * @param dateStr - 변환할 날짜 문자열 (RFC 3339 형식: yyyy-MM-dd'T'HH:mm:ssXXX)
 * @param toTimezone - 대상 시간대 (기본값: 'Asia/Seoul')
 * @returns RFC 3339 형식의 날짜 문자열 (예: "2024-01-15T10:30:00+09:00")
 * @throws {Error} 유효하지 않은 날짜인 경우
 *
 * @example
 * ```typescript
 * // UTC를 KST로 변환
 * formatTimezoneConverter('2024-01-15T01:00:00+00:00', 'Asia/Seoul');
 * // "2024-01-15T10:00:00+09:00"
 *
 * // KST를 UTC로 변환
 * formatTimezoneConverter('2024-01-15T10:00:00+09:00', 'UTC');
 * // "2024-01-15T01:00:00+00:00"
 *
 * // 다른 타임존 간 변환
 * formatTimezoneConverter('2024-01-15T10:00:00+09:00', 'America/New_York');
 * // "2024-01-14T20:00:00-05:00" (또는 -04:00, 서머타임에 따라)
 * ```
 */
export function formatTimezoneConverter(dateStr, toTimezone = 'Asia/Seoul') {
    // RFC 3339 형식 문자열을 Date 객체로 파싱 (타임존 정보 자동 인식)
    const date = parseISO(dateStr);
    if (isNaN(date.getTime())) {
        throw new Error('Invalid date');
    }
    // 원하는 타임존으로 변환하여 RFC 3339 형식으로 포맷팅
    return formatInTimeZone(date, toTimezone, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");
}
/**
 * 날짜 문자열에 시작 시간 또는 종료 시간을 추가합니다.
 * @param dateStr - 날짜 문자열 (yyyy-MM-dd 형식)
 * @param type - 시간 타입 ('start' 또는 'end')
 * @returns 날짜와 시간이 포함된 문자열 (yyyy-MM-ddTHH:mm:ss 형식)
 * @throws {Error} 유효하지 않은 날짜 형식인 경우
 *
 * @example
 * ```typescript
 * // 시작 시간 추가
 * addTimeToDate('2024-01-15', 'start');
 * // "2024-01-15T00:00:00"
 *
 * // 종료 시간 추가
 * addTimeToDate('2024-01-15', 'end');
 * // "2024-01-15T23:59:59"
 * ```
 */
export function addTimeToDate(dateStr, type) {
    // 날짜 형식 검증 (yyyy-MM-dd)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        throw new Error('Invalid date format. Expected format: yyyy-MM-dd');
    }
    // 날짜 유효성 검증
    const date = parseISO(dateStr);
    if (isNaN(date.getTime())) {
        throw new Error('Invalid date');
    }
    // 타입에 따라 시간 추가
    if (type === 'start') {
        return `${dateStr}T00:00:00`;
    }
    else {
        return `${dateStr}T23:59:59`;
    }
}
/**
 * 날짜 문자열에 시작 시간 또는 종료 시간을 추가한 후 타임존을 변환하여 RFC 3339 형식으로 반환합니다.
 * @param dateStr - 날짜 문자열 (yyyy-MM-dd 형식)
 * @param type - 시간 타입 ('start' 또는 'end')
 * @param toTimezone - 대상 시간대 (기본값: 'Asia/Seoul')
 * @returns RFC 3339 형식의 날짜 문자열 (예: "2024-01-15T00:00:00+09:00")
 * @throws {Error} 유효하지 않은 날짜 형식인 경우
 *
 * @example
 * ```typescript
 * // 시작 시간 추가 후 KST로 변환
 * addTimeAndConvertTimezone('2024-01-15', 'start');
 * // "2024-01-15T00:00:00+09:00"
 *
 * // 종료 시간 추가 후 UTC로 변환
 * addTimeAndConvertTimezone('2024-01-15', 'end', 'UTC');
 * // "2024-01-15T23:59:59+00:00"
 * ```
 */
export function addTimeAndConvertTimezone(dateStr, type, toTimezone = 'Asia/Seoul') {
    // 먼저 시간 추가
    const dateTimeStr = addTimeToDate(dateStr, type);
    // 타임존 변환하여 RFC 3339 형식으로 반환
    return formatTimezoneConverter(dateTimeStr, toTimezone);
}
/**
 * YYYYMMDDHHmmssSSS 형식의 날짜 문자열을 RFC 3339 형식으로 변환합니다.
 * 서버에서 보내는 날짜는 UTC 기준으로 간주합니다.
 * @param dateTimeStr - YYYYMMDDHHmmssSSS 형식의 날짜 문자열 (예: "20251205093508119")
 * @param timezone - 날짜의 시간대 (기본값: 'UTC')
 * @returns RFC 3339 형식의 날짜 문자열 (예: "2025-12-05T09:35:08.119Z")
 *
 * @example
 * ```typescript
 * parseDateTimeString('20251205093508119'); // "2025-12-05T09:35:08.119Z"
 * parseDateTimeString('20251205093508'); // "2025-12-05T09:35:08Z"
 * parseDateTimeString('20251205'); // "2025-12-05T00:00:00Z"
 * ```
 */
export function parseDateTimeString(dateTimeStr, timezone = 'UTC') {
    if (!dateTimeStr) {
        return '';
    }
    const str = dateTimeStr.trim();
    let isoString = '';
    // YYYYMMDDHHmmssSSS (17자리)
    if (str.length === 17) {
        const year = str.substring(0, 4);
        const month = str.substring(4, 6);
        const day = str.substring(6, 8);
        const hour = str.substring(8, 10);
        const minute = str.substring(10, 12);
        const second = str.substring(12, 14);
        const millisecond = str.substring(14, 17);
        isoString = `${year}-${month}-${day}T${hour}:${minute}:${second}.${millisecond}`;
    }
    // YYYYMMDDHHmmss (14자리)
    else if (str.length === 14) {
        const year = str.substring(0, 4);
        const month = str.substring(4, 6);
        const day = str.substring(6, 8);
        const hour = str.substring(8, 10);
        const minute = str.substring(10, 12);
        const second = str.substring(12, 14);
        isoString = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
    }
    // YYYYMMDD (8자리)
    else if (str.length === 8) {
        const year = str.substring(0, 4);
        const month = str.substring(4, 6);
        const day = str.substring(6, 8);
        isoString = `${year}-${month}-${day}T00:00:00`;
    }
    // 다른 형식은 그대로 반환
    else {
        return str;
    }
    // ISO 문자열을 파싱하고 타임존 정보를 추가하여 RFC 3339 형식으로 변환
    // 서버에서 보내는 날짜는 UTC이므로, 타임존 정보가 없는 경우 UTC로 명시적으로 파싱
    // parseISO는 타임존 정보가 없으면 로컬 타임존으로 해석하므로, UTC임을 명시하기 위해 'Z' 추가
    const date = parseISO(timezone === 'UTC' ? `${isoString}Z` : isoString);
    if (isNaN(date.getTime())) {
        return str;
    }
    // RFC 3339 형식으로 변환 (타임존 정보 포함)
    return formatInTimeZone(date, timezone, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");
}
/**
 * 날짜 문자열을 UTC ISO 형식으로 변환합니다.
 * 입력 문자열의 타임존 정보를 자동으로 인식하여 UTC로 변환한 후 ISO 형식으로 반환합니다.
 * @param dateStr - 변환할 날짜 문자열 (RFC 3339 또는 ISO 8601 형식)
 * @returns UTC ISO 형식의 날짜 문자열 (예: "2024-01-15T01:00:00.000Z")
 * @throws {Error} 유효하지 않은 날짜인 경우
 *
 * @example
 * ```typescript
 * // KST를 UTC ISO로 변환
 * convertToUtcISO('2025-12-10T05:56:19.500+09:00');
 * // "2025-12-09T20:56:19.500Z"
 *
 * // UTC ISO 문자열은 그대로 반환
 * convertToUtcISO('2025-12-10T05:55:46.101Z');
 * // "2025-12-10T05:55:46.101Z"
 *
 * // 다른 타임존도 자동 변환
 * convertToUtcISO('2025-12-10T05:56:19.500-05:00');
 * // "2025-12-10T10:56:19.500Z"
 * ```
 */
export function convertToUtcISO(dateStr) {
    if (!dateStr) {
        throw new Error('Date string is required');
    }
    // RFC 3339 또는 ISO 8601 형식 문자열을 Date 객체로 파싱 (타임존 정보 자동 인식)
    const date = parseISO(dateStr);
    if (isNaN(date.getTime())) {
        throw new Error('Invalid date');
    }
    // UTC ISO 형식으로 변환 (Z로 끝나는 형식)
    return date.toISOString();
}
//# sourceMappingURL=date.js.map